import React, { useMemo, useCallback } from "react";
import classNames from "classnames";
import styles from "./SubcategoriesBox.module.scss";
// TODO: Исправьте путь импорта useServices на актуальный для вашего проекта
// import { useServices } from "@/services/useServices";

// Временно используем заглушку для useServices, чтобы не было ошибки импорта
const useServices = () => ({ gtag: undefined as any });

import type { Categories } from "@/services/categories";
import type { Gtag } from "@/services/gtag";

export interface MenuItem {
  key: string;
  name: string;
  url?: string;
  level?: number;
  children?: MenuItem[];
  weight?: number;
}

interface SubcategoriesBoxProps {
  menu: Categories;
  rootId: string;
  onClose?: () => void;
}

export const SubcategoriesBox: React.FC<SubcategoriesBoxProps> = ({ menu, rootId, onClose }) => {
  const services = useServices();
  // Gtag из services, SSR-safe
  const gtag: Gtag | undefined = services?.gtag;

  const { title, columns } = useMemo(() => {
    const rootItem = menu.getItem(rootId);
    if (!rootItem) return { title: "", columns: [[], [], []] };
    const title = rootItem.name;
    const sortedGroups = menu
      .getChilds(rootId)
      .map((primary) => {
        const children = menu.getChilds(primary.key);
        return {
          ...primary,
          level: 1,
          children,
          weight: children.length + 1,
        };
      })
      .sort((a, b) => (b.weight ?? 0) - (a.weight ?? 0));
    const COLUMN_COUNT = 3;
    const columns: MenuItem[][] = Array.from({ length: COLUMN_COUNT }, () => []);
    const columnWeights = new Array(COLUMN_COUNT).fill(0);
    for (const group of sortedGroups) {
      const minIndex = columnWeights.indexOf(Math.min(...columnWeights));
      columns[minIndex].push(group);
      columnWeights[minIndex] += group.weight ?? 0;
    }
    return { title, columns };
  }, [menu, rootId]);

  const handleItemClick = useCallback(
    (key: string, event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      const category = menu.getItem(key);
      // SSR-safe вызов Gtag
      if (gtag && category && typeof (gtag as any).pushEvent === 'function') {
        (gtag as any).pushEvent("subcategory_click", category.name);
      } else if (typeof (window as any)?.Gtag?.pushEvent === 'function' && category) {
        (window as any).Gtag.pushEvent("subcategory_click", category.name);
      }
      if (onClose) onClose();
    },
    [menu, gtag, onClose]
  );

  const renderColumnItem = (item: MenuItem) => {
    const level = `l${item.level}`;
    return (
      <a
        className={classNames(styles.item, styles[level])}
        href={item.url}
        onClick={(e) => handleItemClick(item.key, e)}
      >
        <label className={styles.label}>{item.name}</label>
      </a>
    );
  };

  const renderColumn = (columnGroups: MenuItem[]) => (
    <div className={styles.column}>
      {columnGroups.map((group) => (
        <div className={styles["category-group"]} key={group.key}>
          {renderColumnItem(group)}
          {group.children?.map((child) => renderColumnItem({ ...child, level: 2 }))}
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.box}>
      <div className={styles.title}>{title}</div>
      <div className={styles.columns}>
        {columns.map((column, idx) => (
          <React.Fragment key={idx}>{renderColumn(column)}</React.Fragment>
        ))}
      </div>
    </div>
  );
};
